import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Switch,
  Text,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  allowedModelsForProvider,
  ANTHROPIC_EFFORT_LEVELS,
  createMailIntakePromptConfigVersion,
  DEFAULT_CUSTOMER_MATCH_PROMPT,
  DEFAULT_FINANCIAL_SNAPSHOT_FIELDS,
  DEFAULT_FINANCIAL_SNAPSHOT_PROMPT,
  DEFAULT_MAIL_INTAKE_PROVIDER,
  defaultModelForProvider,
  FIELD_KIND_GENERIC,
  getLatestMailIntakePromptConfig,
  getMailIntakePromptConfigVersion,
  listMailIntakePromptConfigVersions,
  MAIL_INTAKE_AI_STAGES,
  MAIL_INTAKE_PROVIDER_ANTHROPIC,
  MAIL_INTAKE_PROVIDERS,
} from 'api/mailIntakePromptConfigApi';
import ContentBox from 'components/ContentBox';
import { useEffect, useState } from 'react';

const KIND_LABELS = {
  generic: 'Number',
  safe: 'Safe (built-in)',
  cohortData: 'Cohort table (built-in)',
};

const blankGenericField = () => ({
  key: '',
  label: '',
  kind: FIELD_KIND_GENERIC,
  instruction: '',
  enabled: true,
});

function MailIntakePromptConfigAdmin() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [summaries, setSummaries] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState('edit');
  const [viewDoc, setViewDoc] = useState(null);
  const [draftCustomerMatchPrompt, setDraftCustomerMatchPrompt] = useState('');
  const [draftFinancialSnapshotPrompt, setDraftFinancialSnapshotPrompt] =
    useState('');
  const [draftFields, setDraftFields] = useState(DEFAULT_FINANCIAL_SNAPSHOT_FIELDS);
  const [draftBankStatementCategories, setDraftBankStatementCategories] =
    useState('revenue, payroll, loan_repayment, loan_drawdown, marketing, rent, supplier, tax, bank_fees, transfer, other');
  const [draftProvider, setDraftProvider] = useState(DEFAULT_MAIL_INTAKE_PROVIDER);
  const [draftModel, setDraftModel] = useState('');
  const [draftEffort, setDraftEffort] = useState('');
  const [draftStageModels, setDraftStageModels] = useState({});
  const [saveNote, setSaveNote] = useState('');
  const [saving, setSaving] = useState(false);

  const bootstrap = async () => {
    setLoading(true);
    try {
      let latest = null;
      try {
        latest = await getLatestMailIntakePromptConfig();
      } catch (err) {
        if (err?.response?.status !== 404) {
          throw err;
        }
      }
      const sum = await listMailIntakePromptConfigVersions();
      setSummaries(sum || []);
      setSelectedVersion('edit');
      setViewDoc(null);
      setDraftCustomerMatchPrompt(
        latest?.customerMatchPrompt || DEFAULT_CUSTOMER_MATCH_PROMPT
      );
      setDraftFinancialSnapshotPrompt(
        latest?.financialSnapshotPrompt || DEFAULT_FINANCIAL_SNAPSHOT_PROMPT
      );
      setDraftFields(
        latest?.fields?.length ? latest.fields : DEFAULT_FINANCIAL_SNAPSHOT_FIELDS
      );
      setDraftBankStatementCategories(
        latest?.bankStatementCategories || 'revenue, payroll, loan_repayment, loan_drawdown, marketing, rent, supplier, tax, bank_fees, transfer, other'
      );
      setDraftProvider(latest?.provider || DEFAULT_MAIL_INTAKE_PROVIDER);
      setDraftModel(latest?.model || '');
      setDraftEffort(latest?.effort || '');
      setDraftStageModels(latest?.stageModels || {});
    } catch (e) {
      toast({
        title: 'Error',
        description: e?.response?.data?.error || e.message,
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onVersionChange = async (value) => {
    setSelectedVersion(value);
    if (value === 'edit') {
      setViewDoc(null);
      return;
    }
    const doc = await getMailIntakePromptConfigVersion(Number(value));
    setViewDoc(doc);
  };

  // Changing provider clears every model selection: a gpt-* id is not valid for Anthropic
  // (and vice versa), and the backend rejects a mismatched pair on save. Clearing rather
  // than remapping leaves each stage on "Default", which resolves to the new provider's
  // own default.
  const onProviderChange = (next) => {
    setDraftProvider(next);
    setDraftModel('');
    setDraftEffort('');
    setDraftStageModels({});
  };

  const updateStageModel = (stageKey, patch) => {
    setDraftStageModels((prev) => {
      const nextStage = { ...(prev[stageKey] || {}), ...patch };
      Object.keys(nextStage).forEach((k) => {
        if (!nextStage[k]) delete nextStage[k];
      });
      const next = { ...prev };
      if (Object.keys(nextStage).length) {
        next[stageKey] = nextStage;
      } else {
        delete next[stageKey];
      }
      return next;
    });
  };

  const updateField = (index, patch) => {
    setDraftFields((prev) =>
      prev.map((f, i) => (i === index ? { ...f, ...patch } : f))
    );
  };

  const addField = () => {
    setDraftFields((prev) => [...prev, blankGenericField()]);
  };

  const removeField = (index) => {
    setDraftFields((prev) => prev.filter((_, i) => i !== index));
  };

  const onSave = async () => {
    if (!draftCustomerMatchPrompt.trim()) {
      toast({ title: 'Customer match prompt required', status: 'warning' });
      return;
    }
    if (!draftFinancialSnapshotPrompt.trim()) {
      toast({ title: 'Financial snapshot prompt required', status: 'warning' });
      return;
    }
    const keys = draftFields.map((f) => f.key.trim());
    if (keys.some((k) => !k)) {
      toast({ title: 'Every field needs a key', status: 'warning' });
      return;
    }
    if (new Set(keys).size !== keys.length) {
      toast({ title: 'Field keys must be unique', status: 'warning' });
      return;
    }

    setSaving(true);
    try {
      await createMailIntakePromptConfigVersion({
        provider: draftProvider,
        model: draftModel || undefined,
        effort: draftEffort || undefined,
        stageModels: Object.keys(draftStageModels).length
          ? draftStageModels
          : undefined,
        customerMatchPrompt: draftCustomerMatchPrompt.trim(),
        financialSnapshotPrompt: draftFinancialSnapshotPrompt.trim(),
        fields: draftFields.map((f) => ({ ...f, key: f.key.trim() })),
        bankStatementCategories: draftBankStatementCategories.trim() || undefined,
        note: saveNote.trim() || undefined,
      });
      toast({ title: 'Saved new version', status: 'success' });
      setSaveNote('');
      await bootstrap();
    } catch (e) {
      toast({
        title: 'Save failed',
        description: e?.response?.data?.error || e.message,
        status: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const readOnly = selectedVersion !== 'edit';
  const displayCustomerMatchPrompt = readOnly
    ? viewDoc?.customerMatchPrompt
    : draftCustomerMatchPrompt;
  const displayFinancialSnapshotPrompt = readOnly
    ? viewDoc?.financialSnapshotPrompt
    : draftFinancialSnapshotPrompt;
  const displayFields = readOnly
    ? viewDoc?.fields || []
    : draftFields;
  const displayBankStatementCategories = readOnly
    ? viewDoc?.bankStatementCategories
    : draftBankStatementCategories;
  const displayProvider = readOnly
    ? viewDoc?.provider || DEFAULT_MAIL_INTAKE_PROVIDER
    : draftProvider;
  const displayModel = readOnly ? viewDoc?.model || '' : draftModel;
  const displayEffort = readOnly ? viewDoc?.effort || '' : draftEffort;
  const displayStageModels = readOnly
    ? viewDoc?.stageModels || {}
    : draftStageModels;
  const isAnthropic = displayProvider === MAIL_INTAKE_PROVIDER_ANTHROPIC;
  const modelOptions = allowedModelsForProvider(displayProvider);
  const providerDefaultModel = defaultModelForProvider(displayProvider);

  return (
    <ContentBox loading={loading} title="Mail Intake">
      <VStack align="stretch" spacing={6}>
        <Text color="gray.600" fontSize="sm">
          Prompts used by the mail-intake pipeline: matching an inbound email to a
          customer when no deterministic rule finds one, and extracting financial
          data from attached documents. Saving always creates a new version — past
          versions are kept for reference and can be viewed (read-only) below.
        </Text>
        <HStack>
          <FormControl maxW="280px">
            <FormLabel>Version</FormLabel>
            <Select
              value={selectedVersion}
              onChange={(e) => onVersionChange(e.target.value)}
            >
              <option value="edit">Edit (from latest)</option>
              {(summaries || []).map((s) => (
                <option key={s.version} value={String(s.version)}>
                  v{s.version}
                  {s.model ? ` — ${s.model}` : ''}
                  {s.note ? ` — ${s.note}` : ''}
                </option>
              ))}
            </Select>
          </FormControl>
        </HStack>
        <Box borderWidth="1px" borderRadius="md" p={4}>
          <Text fontWeight="semibold" mb={1}>
            AI provider &amp; model
          </Text>
          <Text fontSize="xs" color="gray.500" mb={4}>
            Which vendor and model the mail-intake pipeline calls. Each stage below can
            override the model; leaving a stage on &ldquo;Default&rdquo; inherits the one
            set here. Changing the provider clears the model selections, since a model id
            is only valid for its own vendor.
          </Text>
          <HStack align="flex-start" spacing={4} mb={4}>
            <FormControl maxW="240px">
              <FormLabel fontSize="sm">Provider</FormLabel>
              <Select
                size="sm"
                value={displayProvider}
                onChange={(e) => onProviderChange(e.target.value)}
                isDisabled={readOnly}
              >
                {MAIL_INTAKE_PROVIDERS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl maxW="240px">
              <FormLabel fontSize="sm">Default model</FormLabel>
              <Select
                size="sm"
                value={displayModel}
                onChange={(e) => setDraftModel(e.target.value)}
                isDisabled={readOnly}
              >
                <option value="">Default ({providerDefaultModel})</option>
                {modelOptions.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Select>
            </FormControl>
            {isAnthropic && (
              <FormControl maxW="200px">
                <FormLabel fontSize="sm">Effort</FormLabel>
                <Select
                  size="sm"
                  value={displayEffort}
                  onChange={(e) => setDraftEffort(e.target.value)}
                  isDisabled={readOnly}
                >
                  <option value="">Default (high)</option>
                  {ANTHROPIC_EFFORT_LEVELS.map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Thinking depth. Lower is cheaper and faster.
                </Text>
              </FormControl>
            )}
          </HStack>

          <Text fontWeight="semibold" fontSize="sm" mb={2}>
            Per-stage overrides
          </Text>
          <VStack align="stretch" spacing={3}>
            {MAIL_INTAKE_AI_STAGES.map((stage) => (
              <HStack key={stage.key} align="flex-start" spacing={4}>
                <Box flex="1" minW={0}>
                  <Text fontSize="sm" fontWeight="medium">
                    {stage.label}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {stage.description}
                  </Text>
                </Box>
                <FormControl maxW="240px">
                  <Select
                    size="sm"
                    value={displayStageModels?.[stage.key]?.model || ''}
                    onChange={(e) =>
                      updateStageModel(stage.key, { model: e.target.value })
                    }
                    isDisabled={readOnly}
                  >
                    <option value="">
                      Default ({displayModel || providerDefaultModel})
                    </option>
                    {modelOptions.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                {isAnthropic && (
                  <FormControl maxW="160px">
                    <Select
                      size="sm"
                      value={displayStageModels?.[stage.key]?.effort || ''}
                      onChange={(e) =>
                        updateStageModel(stage.key, { effort: e.target.value })
                      }
                      isDisabled={readOnly}
                    >
                      <option value="">Effort: default</option>
                      {ANTHROPIC_EFFORT_LEVELS.map((level) => (
                        <option key={level} value={level}>
                          Effort: {level}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </HStack>
            ))}
          </VStack>
        </Box>
        <FormControl>
          <FormLabel>Bank statement categories</FormLabel>
          <Text fontSize="xs" color="gray.500" mb={2}>
            Categories the AI may assign to a transaction read out of a bank
            statement, comma separated. Adding one here is enough — the next
            extraction will try to match it, with no deploy. Names are matched
            loosely, so <code>Loan Repayment</code> and{' '}
            <code>loan_repayment</code> are the same category.{' '}
            <code>other</code> is always available as the fallback for anything
            that does not fit, whether or not it is listed.
          </Text>
          <Textarea
            value={displayBankStatementCategories || ''}
            onChange={(e) => setDraftBankStatementCategories(e.target.value)}
            isReadOnly={readOnly}
            minH="80px"
            fontFamily="mono"
            fontSize="sm"
          />
        </FormControl>
        <FormControl>
          <FormLabel>Customer match prompt</FormLabel>
          <Text fontSize="xs" color="gray.500" mb={2}>
            Used as the last-resort AI step to identify which customer an email is
            from/about. Placeholders (filled in automatically at send time):{' '}
            <code>{'{{candidates}}'}</code>, <code>{'{{subject}}'}</code>,{' '}
            <code>{'{{from}}'}</code>, <code>{'{{to}}'}</code>,{' '}
            <code>{'{{cc}}'}</code>, <code>{'{{bcc}}'}</code>,{' '}
            <code>{'{{body}}'}</code>.
          </Text>
          <Textarea
            value={displayCustomerMatchPrompt || ''}
            onChange={(e) => setDraftCustomerMatchPrompt(e.target.value)}
            isReadOnly={readOnly}
            minH="320px"
            fontFamily="mono"
            fontSize="sm"
          />
        </FormControl>
        <FormControl>
          <FormLabel>Financial snapshot extraction prompt</FormLabel>
          <Text fontSize="xs" color="gray.500" mb={2}>
            Used to extract financial fields from a matched customer&apos;s attached
            documents. Placeholders: <code>{'{{customerId}}'}</code>,{' '}
            <code>{'{{customerName}}'}</code>,{' '}
            <code>{'{{fieldInstructions}}'}</code> (auto-generated from the fields
            below), and <code>{'{{attachmentList}}'}</code> (the list of attached
            filenames).
          </Text>
          <Textarea
            value={displayFinancialSnapshotPrompt || ''}
            onChange={(e) => setDraftFinancialSnapshotPrompt(e.target.value)}
            isReadOnly={readOnly}
            minH="240px"
            fontFamily="mono"
            fontSize="sm"
          />
        </FormControl>
        <Box>
          <FormLabel>Financial snapshot fields</FormLabel>
          <Text fontSize="xs" color="gray.500" mb={2}>
            Which fields ChatGPT is asked to extract, and the wording spliced into{' '}
            <code>{'{{fieldInstructions}}'}</code> for each. Number fields can be
            freely added, reworded, renamed, or removed. Safe and Cohort Data have a
            fixed, structured response shape wired into the rest of the app — their
            wording can be edited and they can be turned on/off, but not removed or
            renamed.
          </Text>
          <Accordion allowToggle>
            {displayFields.map((field, index) => {
              const isGeneric = field.kind === FIELD_KIND_GENERIC;
              return (
                // eslint-disable-next-line react/no-array-index-key
                <AccordionItem key={index}>
                  <AccordionButton>
                    <Flex flex="1" align="center" gap={2} textAlign="left">
                      <Text fontSize="sm" fontWeight={500}>
                        {field.label || field.key || '(untitled field)'}
                      </Text>
                      <Badge colorScheme={isGeneric ? 'gray' : 'purple'} fontSize="xs">
                        {KIND_LABELS[field.kind] || field.kind}
                      </Badge>
                      {!field.enabled && (
                        <Badge colorScheme="red" fontSize="xs">
                          Disabled
                        </Badge>
                      )}
                    </Flex>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <VStack align="stretch" spacing={3}>
                      <Flex align="center" gap={4} wrap="wrap">
                        <FormControl display="flex" alignItems="center" w="auto">
                          <FormLabel mb={0} fontSize="sm">
                            Enabled
                          </FormLabel>
                          <Switch
                            isChecked={field.enabled}
                            onChange={(e) =>
                              updateField(index, { enabled: e.target.checked })
                            }
                            isDisabled={readOnly}
                          />
                        </FormControl>
                        {isGeneric && (
                          <FormControl w="220px">
                            <FormLabel fontSize="xs">Key</FormLabel>
                            <Input
                              value={field.key}
                              onChange={(e) =>
                                updateField(index, { key: e.target.value })
                              }
                              isReadOnly={readOnly}
                              placeholder="e.g. runway"
                              fontFamily="mono"
                              fontSize="sm"
                            />
                          </FormControl>
                        )}
                        <FormControl flex="1" minW="220px">
                          <FormLabel fontSize="xs">Label</FormLabel>
                          <Input
                            value={field.label}
                            onChange={(e) =>
                              updateField(index, { label: e.target.value })
                            }
                            isReadOnly={readOnly}
                            fontSize="sm"
                          />
                        </FormControl>
                        {isGeneric && !readOnly && (
                          <Button
                            size="sm"
                            colorScheme="red"
                            variant="outline"
                            onClick={() => removeField(index)}
                            alignSelf="flex-end"
                          >
                            Delete field
                          </Button>
                        )}
                      </Flex>
                      <FormControl>
                        <FormLabel fontSize="xs">Instruction</FormLabel>
                        <Textarea
                          value={field.instruction}
                          onChange={(e) =>
                            updateField(index, { instruction: e.target.value })
                          }
                          isReadOnly={readOnly}
                          minH="100px"
                          fontFamily="mono"
                          fontSize="sm"
                        />
                      </FormControl>
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
              );
            })}
          </Accordion>
          {!readOnly && (
            <Button size="sm" variant="outline" mt={3} onClick={addField}>
              + Add field
            </Button>
          )}
        </Box>
        {!readOnly && (
          <>
            <FormControl>
              <FormLabel>Version note (optional)</FormLabel>
              <Textarea
                value={saveNote}
                onChange={(e) => setSaveNote(e.target.value)}
                placeholder="What changed in this version?"
              />
            </FormControl>
            <Button colorScheme="purple" onClick={onSave} isLoading={saving}>
              Save as new version
            </Button>
          </>
        )}
        {readOnly && viewDoc && (
          <Text fontSize="sm" color="gray.500">
            Created {new Date(viewDoc.createdAt).toLocaleString()} by{' '}
            {viewDoc.createdBy || 'unknown'}
          </Text>
        )}
      </VStack>
    </ContentBox>
  );
}

export default MailIntakePromptConfigAdmin;
